'use client';

import { cn } from '@/lib/utils';

/**
 * Material 3 Expressive ("Android 16") loading indicator: a filled blob that
 * continuously morphs through rounded polygon shapes while spinning.
 *
 * Every keyframe path is sampled with the same point count so SMIL can
 * interpolate between them; the morph is driven by <animate>, the rotation
 * by CSS (see .animate-spinner-rotate in globals.css).
 */

// r(θ) = R·(1 + amp·cos(lobes·θ + phase)) — a scalloped blob; amp 0 is a circle
function blobPath(lobes: number, amp: number, phase: number): string {
  const POINTS = 72;
  const R = 42;
  const parts: string[] = [];
  for (let i = 0; i < POINTS; i++) {
    const t = (i / POINTS) * 2 * Math.PI;
    const r = R * (1 + amp * Math.cos(lobes * t + phase));
    const x = 50 + r * Math.cos(t);
    const y = 50 + r * Math.sin(t);
    parts.push(`${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`);
  }
  return parts.join('') + 'Z';
}

const SHAPES = [
  blobPath(0, 0, 0), // circle
  blobPath(3, 0.16, 0), // rounded triangle
  blobPath(4, 0.13, 1.1), // rounded square
  blobPath(5, 0.13, 2.2), // pentagon
  blobPath(6, 0.14, 0.6), // cookie
  blobPath(8, 0.16, 1.7), // soft burst
];

// Loop back to the first shape for a seamless cycle
const MORPH_VALUES = [...SHAPES, SHAPES[0]].join(';');
const MORPH_SPLINES = Array(SHAPES.length).fill('0.4 0 0.2 1').join(';');

export function MaterialSpinner({ className }: { className?: string }) {
  return (
    <svg
      className={cn('h-5 w-5 animate-spinner-rotate text-primary', className)}
      viewBox="0 0 100 100"
      role="status"
      aria-label="Загрузка"
    >
      <path fill="currentColor" d={SHAPES[0]}>
        <animate
          attributeName="d"
          dur="4.2s"
          repeatCount="indefinite"
          calcMode="spline"
          keySplines={MORPH_SPLINES}
          values={MORPH_VALUES}
        />
      </path>
    </svg>
  );
}
